import { addDoc, collection, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore'
import { db } from '../firebase'

function toYMD(d) {
  const date = d instanceof Date ? d : new Date(d)
  return date.toISOString().slice(0, 10)
}

function startOfDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfDay(date) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

function normalizePaymentMethod(method) {
  const m = String(method || '').toLowerCase()
  if (m === 'account' || m.includes('on_account') || m.includes('on account')) return 'account'
  if (m.includes('cash')) return 'cash'
  if (m.includes('card')) return 'card'
  if (m.includes('eft') || m.includes('bank')) return 'eft'
  return 'other'
}

function getCreatedAtDate(txn) {
  const ts = txn?.createdAt
  if (!ts) return null
  if (typeof ts.toDate === 'function') return ts.toDate()
  return new Date(ts)
}

/** Legacy POS docs may omit `status`; treat as counted when missing. */
function isCountedTransaction(txn) {
  const s = txn?.status
  if (s == null || s === '') return true
  return String(s).toLowerCase() === 'completed'
}

/** Map cart / POS line item to a breakdown bucket */
function normalizeItemCategory(cat) {
  const c = String(cat || '').toLowerCase()
  if (c === 'wash' || c === 'service') return 'wash'
  if (c === 'product' || c === 'retail') return 'product'
  if (c === 'subscription') return 'subscription'
  if (c === 'addon' || c === 'add_on' || c === 'add-on' || c === 'extra') return 'addon'
  if (c === 'custom') return 'custom'
  return 'other'
}

function inferCategory(item) {
  const id = String(item?.id || '')
  if (id.startsWith('product_')) return 'product'
  if (id.startsWith('wash_') || id.startsWith('washcat_')) return 'wash'

  const cat = normalizeItemCategory(item?.category ?? item?.type)
  if (cat !== 'other' && cat !== 'custom') return cat
  const name = String(item?.name || item?.description || '').toLowerCase()
  if (name.includes('subscription') || name.includes('unlimited')) return 'subscription'
  if (name.includes('wash') || name.includes('polish') || name.includes('valet') || name.includes('interior')) {
    return 'wash'
  }
  if (cat === 'custom') return 'other'
  if (item?.category) return 'product'
  return cat
}

function lineAmount(item, qty) {
  const total = Number(item?.total)
  if (Number.isFinite(total) && total !== 0) return total
  const price = Number(item?.price ?? item?.unitPrice ?? 0) || 0
  return qty * price
}

function addBucketRow(map, name, qty, amount) {
  const key = name || 'Item'
  if (!map[key]) map[key] = { name: key, qty: 0, amount: 0 }
  map[key].qty += qty
  map[key].amount += amount
}

function bucketToSortedArray(counts) {
  return Object.values(counts)
    .map((row) => ({
      name: row.name,
      qty: Number(row.qty) || 0,
      amount: Number(row.amount) || 0,
    }))
    .filter((row) => row.qty > 0)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

/**
 * Aggregate qty + amount sold by wash type / product across POS lines (and bay washes).
 */
function summarizeSalesBreakdown(transactions, dayWashes = []) {
  const washes = {}
  const products = {}
  const subscriptions = {}
  const addons = {}
  const otherLines = {}

  const queueWashes = (dayWashes || []).filter((w) => String(w.status || '') !== 'cancelled')
  queueWashes.forEach((w) => {
    const name = String(w.serviceType || w.serviceName || 'Wash').trim() || 'Wash'
    addBucketRow(washes, name, 1, Number(w.amount || 0) || 0)
  })

  transactions.forEach((t) => {
    if (!isCountedTransaction(t)) return
    const items = Array.isArray(t.items) ? t.items : []
    items.forEach((item) => {
      const qty = Number(item.quantity ?? item.qty ?? 1) || 1
      const rawName = String(item.name ?? item.description ?? '').trim()
      const name = rawName || 'Item'
      const amount = lineAmount(item, qty)
      const cat = inferCategory(item)
      const fromQueueWash = String(item.id || '').startsWith('wash_') && queueWashes.length > 0

      if (cat === 'wash' && fromQueueWash) return
      if (cat === 'wash') addBucketRow(washes, name, qty, amount)
      else if (cat === 'product') addBucketRow(products, name, qty, amount)
      else if (cat === 'subscription') addBucketRow(subscriptions, name, qty, amount)
      else if (cat === 'addon') addBucketRow(addons, name, qty, amount)
      else addBucketRow(otherLines, name, qty, amount)
    })
  })

  return {
    washes: bucketToSortedArray(washes),
    products: bucketToSortedArray(products),
    subscriptions: bucketToSortedArray(subscriptions),
    addons: bucketToSortedArray(addons),
    otherLines: bucketToSortedArray(otherLines),
  }
}

/** Plain-text breakdown for slips / mailto body */
export function formatCashUpBreakdownText({
  washes = [],
  products = [],
  subscriptions = [],
  addons = [],
  otherLines = [],
  accountTotal = 0,
  accountTransactionCount = 0,
}) {
  const lines = []
  const rowLine = (r) => {
    const amt = Number(r.amount || 0)
    const amtPart = amt ? ` · R${amt.toFixed(2)}` : ''
    return `  • ${r.name} — qty ${r.qty}${amtPart}`
  }

  lines.push('Items sold:')
  lines.push('')

  if ((washes || []).length) {
    lines.push('Washes:')
    washes.forEach((r) => lines.push(rowLine(r)))
    lines.push('')
  } else {
    lines.push('Washes: none')
    lines.push('')
  }

  if ((products || []).length) {
    lines.push('Products:')
    products.forEach((r) => lines.push(rowLine(r)))
    lines.push('')
  } else {
    lines.push('Products: none')
    lines.push('')
  }

  if ((addons || []).length) {
    lines.push('Add-ons:')
    addons.forEach((r) => lines.push(rowLine(r)))
    lines.push('')
  }

  if ((subscriptions || []).length) {
    lines.push('Subscriptions:')
    subscriptions.forEach((r) => lines.push(rowLine(r)))
    lines.push('')
  }

  if ((otherLines || []).length) {
    lines.push('Other:')
    otherLines.forEach((r) => lines.push(rowLine(r)))
    lines.push('')
  }

  lines.push('Accounts (on account — not in till total):')
  lines.push(`  • Transactions: ${accountTransactionCount}`)
  lines.push(`  • Total: R${Number(accountTotal || 0).toFixed(2)}`)

  return lines.join('\n')
}

export function cashUpBreakdownFromRecord(cashUp) {
  return {
    washes: cashUp?.washBreakdown || [],
    products: cashUp?.productBreakdown || [],
    addons: cashUp?.addonBreakdown || [],
    subscriptions: cashUp?.subscriptionBreakdown || [],
    otherLines: cashUp?.otherLineBreakdown || [],
  }
}

export function parseMoneyInput(value) {
  const n = Number(String(value ?? '').replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

export function tillGrandFrom(parts) {
  return (
    Number(parts?.cash || 0) +
    Number(parts?.card || 0) +
    Number(parts?.eft || 0) +
    Number(parts?.other || 0)
  )
}

export function variance(counted, expected) {
  return Number(counted || 0) - Number(expected || 0)
}

export function formatVariance(diff) {
  const n = Number(diff || 0)
  if (Math.abs(n) < 0.005) return 'R0.00'
  const sign = n > 0 ? '+' : '-'
  return `${sign}R${Math.abs(n).toFixed(2)}`
}

export function countedTotalsFromCashUp(c) {
  const expected = {
    cash: Number(c?.cashTotal || 0),
    card: Number(c?.cardTotal || 0),
    eft: Number(c?.eftTotal || 0),
    other: Number(c?.otherTotal || 0),
  }
  const counted = {
    cash: c?.countedCash != null ? Number(c.countedCash) : expected.cash,
    card: c?.countedCard != null ? Number(c.countedCard) : expected.card,
    eft: c?.countedEft != null ? Number(c.countedEft) : expected.eft,
    other: c?.countedOther != null ? Number(c.countedOther) : expected.other,
  }
  return { expected, counted }
}

export const cashUpService = {
  async getDayTransactions({ branchId, date }) {
    const from = startOfDay(date)
    const to = endOfDay(date)

    // Some parts of the app write POS sales to `pos_transactions`, other parts to `transactions`.
    // We aggregate both so cash-ups always reflect the full POS day.
    const posTxQ = query(
      collection(db, 'pos_transactions'),
      where('branchId', '==', branchId),
      where('createdAt', '>=', from),
      where('createdAt', '<=', to),
      orderBy('createdAt', 'desc'),
      limit(2000)
    )

    const txQ = query(
      collection(db, 'transactions'),
      where('branchId', '==', branchId),
      where('createdAt', '>=', from),
      where('createdAt', '<=', to),
      orderBy('createdAt', 'desc'),
      limit(2000)
    )

    const [posSnap, txSnap] = await Promise.all([getDocs(posTxQ), getDocs(txQ)])
    const posTransactions = posSnap.docs.map((d) => ({ id: d.id, ...d.data(), _source: 'pos_transactions' }))
    const transactions = txSnap.docs.map((d) => ({ id: d.id, ...d.data(), _source: 'transactions' }))

    return [...posTransactions, ...transactions].sort((a, b) => {
      const da = getCreatedAtDate(a)?.getTime() || 0
      const dbb = getCreatedAtDate(b)?.getTime() || 0
      return dbb - da
    })
  },

  async getDayWashes({ branchId, date }) {
    const from = startOfDay(date)
    const to = endOfDay(date)
    try {
      const washQ = query(
        collection(db, 'washes'),
        where('branchId', '==', branchId),
        where('createdAt', '>=', from),
        where('createdAt', '<=', to),
        orderBy('createdAt', 'desc'),
        limit(2000)
      )
      const snap = await getDocs(washQ)
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    } catch (error) {
      console.warn('Cash-up wash query fell back to unfiltered branch load:', error)
      try {
        const washQ = query(
          collection(db, 'washes'),
          where('branchId', '==', branchId),
          limit(2000)
        )
        const snap = await getDocs(washQ)
        return snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((w) => {
            const dt = getCreatedAtDate(w)
            if (!dt) return false
            return dt >= from && dt <= to
          })
      } catch (e) {
        console.error('Failed to load washes for cash-up breakdown:', e)
        return []
      }
    }
  },

  /** Qty + amount aggregates for preview / reporting. */
  buildSalesBreakdown(transactions, dayWashes = []) {
    return summarizeSalesBreakdown(transactions, dayWashes)
  },

  summarizeTransactions(transactions) {
    const summary = {
      cashTotal: 0,
      cardTotal: 0,
      eftTotal: 0,
      accountTotal: 0,
      otherTotal: 0,
      transactionCount: 0,
      accountTransactionCount: 0,
      bySource: {},
    }

    transactions.forEach((t) => {
      if (!isCountedTransaction(t)) return

      const total = Number(t.total ?? t.amount ?? 0) || 0
      const bucket = normalizePaymentMethod(t.paymentMethod)

      if (bucket === 'account') {
        summary.accountTotal += total
        summary.accountTransactionCount += 1
      } else if (bucket === 'cash') summary.cashTotal += total
      else if (bucket === 'card') summary.cardTotal += total
      else if (bucket === 'eft') summary.eftTotal += total
      else summary.otherTotal += total

      summary.transactionCount += 1
      const src = t._source || 'unknown'
      summary.bySource[src] = (summary.bySource[src] || 0) + 1
    })

    return summary
  },

  async hasCashUpForDay({ branchId, cashUpDate }) {
    const q = query(
      collection(db, 'cashUps'),
      where('branchId', '==', branchId),
      where('cashUpDate', '==', cashUpDate),
      limit(1)
    )
    const snap = await getDocs(q)
    return !snap.empty
  },

  async createCashUpFromPos({
    branchId,
    branchName,
    date,
    createdByUid,
    createdByName,
    createdByEmail,
    notes = '',
    counted = null,
  }) {
    const cashUpDate = toYMD(date)
    const from = startOfDay(date)
    const to = endOfDay(date)

    const already = await this.hasCashUpForDay({ branchId, cashUpDate })
    if (already) {
      throw new Error(`Cash-up already exists for ${cashUpDate}`)
    }

    const transactions = await this.getDayTransactions({ branchId, date })
    const dayWashes = await this.getDayWashes({ branchId, date })
    const totals = this.summarizeTransactions(transactions)
    const breakdown = summarizeSalesBreakdown(transactions, dayWashes)

    const countedCash = counted?.cash != null ? Number(counted.cash) : totals.cashTotal
    const countedCard = counted?.card != null ? Number(counted.card) : totals.cardTotal
    const countedEft = counted?.eft != null ? Number(counted.eft) : totals.eftTotal
    const countedOther = counted?.other != null ? Number(counted.other) : totals.otherTotal

    const record = {
      branchId,
      branchName: branchName || '',
      cashUpDate,
      periodStart: from,
      periodEnd: to,
      createdByUid,
      createdByName: createdByName || '',
      createdByEmail: createdByEmail || '',
      cashTotal: totals.cashTotal,
      cardTotal: totals.cardTotal,
      eftTotal: totals.eftTotal,
      otherTotal: totals.otherTotal,
      countedCash,
      countedCard,
      countedEft,
      countedOther,
      countedByUid: createdByUid || '',
      countedByName: createdByName || '',
      countedAt: serverTimestamp(),
      accountTotal: totals.accountTotal,
      accountTransactionCount: totals.accountTransactionCount,
      transactionCount: totals.transactionCount,
      washBreakdown: breakdown.washes,
      productBreakdown: breakdown.products,
      subscriptionBreakdown: breakdown.subscriptions,
      addonBreakdown: breakdown.addons,
      otherLineBreakdown: breakdown.otherLines,
      sources: totals.bySource,
      notes: notes || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const cashUpRef = await addDoc(collection(db, 'cashUps'), record)
    return {
      id: cashUpRef.id,
      totals,
      breakdown,
      counted: { cash: countedCash, card: countedCard, eft: countedEft, other: countedOther },
    }
  },

  /** Update physically counted till amounts. Does not change POS transactions or system totals. */
  async updateCountedAmounts({
    cashUpId,
    countedCash,
    countedCard,
    countedEft,
    countedOther,
    countedByUid,
    countedByName,
  }) {
    if (!cashUpId) throw new Error('Missing cash-up id')
    await updateDoc(doc(db, 'cashUps', cashUpId), {
      countedCash: Number(countedCash || 0),
      countedCard: Number(countedCard || 0),
      countedEft: Number(countedEft || 0),
      countedOther: Number(countedOther || 0),
      countedByUid: countedByUid || '',
      countedByName: countedByName || '',
      countedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  },

  // No PDF files are stored for cash-ups (reprint from saved data instead).
}

